import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Menu, Globe, Sun, Moon, LogOut, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { NAV, navLabel, groupLabel } from "@/lib/i18n";
import VoiceAssistant from "@/components/VoiceAssistant";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} aria-hidden="true" />;
}

export default function Layout({ children }) {
  const { user, logout, lang, toggleLang, theme, toggleTheme, t, dir } = useApp();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Demo banner */}
      <div data-testid="env-banner" className="sticky top-0 z-40 flex items-center justify-center gap-2 bg-blue-950/70 px-4 py-1.5 text-center text-xs font-medium text-blue-100 border-b border-blue-800">
        <Icons.FlaskConical className="h-3.5 w-3.5" /> {t("demoBanner")}
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed z-30 h-[calc(100vh-2rem)] w-72 shrink-0 overflow-y-auto border-border glass p-4 transition-transform lg:sticky lg:top-8 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"} ${dir === "rtl" ? "border-l" : "border-r"}`}
          style={{ insetInlineStart: 0 }}>
          <div className="mb-6 flex items-center gap-3 px-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground font-bold">IT</div>
            <div>
              <div className="text-sm font-bold leading-tight">{t("brand")}</div>
              <div className="text-[10px] text-muted-foreground">{t("tagline")}</div>
            </div>
          </div>
          <nav className="space-y-5">
            {NAV.map((section) => (
              <div key={section.group.en}>
                <div className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{groupLabel(section.group, lang)}</div>
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavLink key={item.key} to={item.path} end={item.path === "/"} data-testid={`nav-${item.key}`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary/20 text-foreground font-semibold border border-primary/40" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                      <Icon name={item.icon} className="h-4 w-4 shrink-0" />
                      <span className="truncate">{navLabel(item, lang)}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>
        {open && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Main */}
        <div className="flex min-h-[calc(100vh-2rem)] flex-1 flex-col">
          <header className="sticky top-8 z-20 flex items-center justify-between gap-3 border-b border-border glass px-4 py-3 lg:px-8">
            <button className="lg:hidden" onClick={() => setOpen((o) => !o)} data-testid="menu-toggle" aria-label="Menu"><Menu className="h-5 w-5" /></button>
            <div className="hidden text-sm font-semibold sm:block">{t("app")}</div>
            <div className="flex items-center gap-2">
              <button data-testid="lang-toggle" onClick={toggleLang} className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary">
                <Globe className="h-4 w-4" /> {lang === "en" ? "العربية" : "English"}
              </button>
              <button data-testid="theme-toggle" onClick={toggleTheme} className="rounded-xl border border-border p-2 hover:bg-secondary" aria-label="Theme">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <div className="flex items-center gap-2 rounded-xl border border-border px-2.5 py-1.5">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground">
                  {(user?.name || "U").slice(0, 1)}
                </div>
                <div className="hidden text-xs sm:block">
                  <div className="font-semibold leading-none">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground">{user?.role}</div>
                </div>
                <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="ms-1 text-muted-foreground hover:text-destructive" aria-label={t("logout")}><LogOut className="h-4 w-4" /></button>
              </div>
            </div>
          </header>
          <main className="flex-1 p-4 lg:p-8 fade-in">{children}</main>
        </div>
      </div>
      <VoiceAssistant />
    </div>
  );
}

export function PageHeader({ title, subtitle, readAloudContext, actions }) {
  const RA = require("@/components/ReadAloud").default;
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">
        {readAloudContext && <RA context={readAloudContext} testId={`ra-${String(title).toLowerCase().replace(/\W+/g, "-")}`} />}
        {actions}
      </div>
    </div>
  );
}
